// src/components/ui/SectionCard.jsx
//
// The section card of the "Gestione Workspace" look (2026-09-27): a surface card
// whose header is a round icon tile, a title, an optional subtitle and an optional
// action on the right; the body is whatever the caller renders below it (callers
// own their body padding, because some bodies are full-bleed tables).
//
// Used by the console tabs AND by the avatar-menu pages (Crediti, Profilo, Dati di
// fatturazione), so the two surfaces share one aesthetic. Piani (2026-10-08) is two of them: the plans card and the
// «Crediti extra» card.
// ⚠️ CompanyWorkspacesPage / CompanyAdminsSection still render the same card markup
// inline rather than through this component.
// ⚠️ `headingLevel`: inside the console the layout owns the <h1>, so a tab passes
// 2; a standalone page has no other title and passes 1. Only the tag and its size
// change — never the words.
//
// `titleAside` (v204): a small control right after the title, outside the heading (Abbonamento's «Disdici» chip).
//
// `PAGE_COLUMN` is the page wrapper both surfaces use: the centred 1280px column.
// `PageHeader` is the page title for a page that holds SEVERAL cards (Knowledge
// Base, Piani's panels): an <h1> in the same 18px display face, a subtitle, and an
// optional control on the right (the superadmin tenant dropdown). A page that is
// ONE card puts its <h1> in the card instead (headingLevel={1}).
//
// ⚠️ 2026-10-01: `GroupPageHeader` (the "Brian Cortex" group header) was REMOVED — Brand Voice, Knowledge
// Base and Buyer Personas now share the console's white band, `PageBand.jsx`; the group name lives in
// the sidebar only.
import React from 'react';
import { COLORS, FONT_SANS, FONT_DISPLAY, RADII, SHADOW } from '../../constants/theme.js';

export const PAGE_COLUMN = {
  padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink,
  maxWidth: '1280px', margin: '0 auto', boxSizing: 'border-box',
};

// The body padding most cards use (full-bleed tables pass none).
export const CARD_BODY = { padding: '0 24px 24px' };

export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      gap: '16px', flexWrap: 'wrap', margin: '0 0 20px',
    }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={{ margin: 0, fontFamily: FONT_DISPLAY, fontSize: '18px', fontWeight: 700, lineHeight: '24px' }}>
          {title}
        </h1>
        {subtitle && <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: '4px 0 0' }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export default function SectionCard({
  icon: Icon, title, subtitle, action, titleAside = null, headingLevel = 2, children, style, as: Tag = 'div',
}) {
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  // A card that is the page's ONLY section (Buyer Personas) needs no header of its own: the page band already
  // names it. With no title, icon or action the header row is not rendered and the body pads itself.
  const hasHeader = Boolean(title || Icon || action);
  return (
    <Tag style={{
      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
      borderRadius: RADII.section, boxShadow: SHADOW.card, overflow: 'hidden',
      ...style,
    }}>
      {hasHeader && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '24px', flexWrap: 'wrap' }}>
          {Icon && (
            <div style={{
              width: '40px', height: '40px', borderRadius: RADII.pill, flexShrink: 0,
              border: `1px solid ${COLORS.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon size={20} color={COLORS.ink} strokeWidth={1.8} aria-hidden="true" />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <Heading style={{
                margin: 0, fontFamily: FONT_DISPLAY, fontWeight: 700,
                fontSize: headingLevel === 1 ? '18px' : '16px', lineHeight: '24px',
              }}>
                {title}
              </Heading>
              {titleAside}
            </div>
            {subtitle && <div style={{ fontSize: '12px', color: COLORS.textTertiary }}>{subtitle}</div>}
          </div>
          {action}
        </div>
      )}
      {children}
    </Tag>
  );
}
